import React from 'react'
import { Form, Row, Col, DropdownButton, Dropdown } from 'react-bootstrap'

// definitions
import { AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { XeroAPIContact } from '../../../../../../back-end/utilities/apiDefinitions'
import { MessageAction } from '../../../ui/messages/message'
import { AppState } from '../../../../App.d'

// utilities
import { XeroContactLiveSearch } from './XeroContactLiveSearch'
import { DetailedStripeOrgAccountXeroOrg } from '../settings'
import { XeroAccountsAndItemsDropdownItems } from './CustomMappings'

interface PaymentsSettingsProps {
	orgMapping: DetailedStripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	stripeOrgID: string
	xeroOrgs: AccountXeroOrgWithData[]
	appState: AppState
	handleSettingsUpdate: (stripeOrgID: string, values: { [name: string]: string | boolean | XeroAPIContact | undefined }) => void
	updateMessages: (action: MessageAction) => void
}

const PaymentsSettings = (props: PaymentsSettingsProps) => {
	const [searchFilter, setSearchFilter] = React.useState<string>('')
	const selectedXeroAccountOrItem =
		props.mappedXeroOrg?.accounts.find((account) => account.xeroAccountID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsXeroAccountID) ||
		props.mappedXeroOrg?.items.find((item) => item.xeroItemID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsXeroItemID)

	return (
		<>
			{props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' && (
				<>
					<Form.Group as={Row} className="d-flex align-items-center" style={{ height: '2.5rem' }}>
						<Col sm="2">
							<Form.Label htmlFor={`${props.stripeOrgID}-payments-enabled`} className="mb-0">
								Enable Xero Invoices for Stripe Payments
							</Form.Label>
						</Col>
						<Col>
							<Form.Check
								type={'switch'}
								id={`${props.stripeOrgID}-payments-enabled`}
								name={'paymentsEnabled'}
								checked={!!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsEnabled}
								onChange={(e) => props.handleSettingsUpdate(props.stripeOrgID, { [e.target.name]: e.target.checked })}
							/>
						</Col>
					</Form.Group>
				</>
			)}
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Account/Product
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-stripe-payments-account-item-select`}
						title={selectedXeroAccountOrItem?.name ?? 'Select...'}
						onSelect={(eventKey) => {
							const xeroAccount = props.mappedXeroOrg?.accounts.find((account) => account.xeroAccountID === eventKey)
							const newValues = eventKey
								? {
										...(xeroAccount
											? {
													paymentsXeroAccountID: eventKey,
													paymentsXeroItemID: null,
											  }
											: {
													paymentsXeroItemID: eventKey,
													paymentsXeroAccountID: null,
											  }),
								  }
								: {}
							props.handleSettingsUpdate(props.stripeOrgID, newValues)
							setSearchFilter('')
						}}
						disabled={
							props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' &&
							!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsEnabled
						}
					>
						<XeroAccountsAndItemsDropdownItems mappedXeroOrg={props.mappedXeroOrg} searchFilter={searchFilter} setSearchFilter={setSearchFilter} />
					</DropdownButton>
				</Col>
			</Form.Group>
			<Form.Group as={Row} style={{ height: '2.5rem' }}>
				<Form.Label column sm={2}>
					Xero Tax Rate
				</Form.Label>
				<Col sm={4}>
					<DropdownButton
						id={`${props.stripeOrgID}-stripe-payments-tax-rate-select`}
						title={
							props.mappedXeroOrg!.taxRates.find(
								(tr) => tr.xeroTaxRateID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsXeroTaxRateID
							)?.name || 'Select Xero Tax Rate'
						}
						onSelect={(eventKey) => props.handleSettingsUpdate(props.stripeOrgID, { paymentsXeroTaxRateID: eventKey! })}
						disabled={
							props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' &&
							!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsEnabled
						}
					>
						{props
							.mappedXeroOrg!.taxRates.filter((tr) => tr.canApplyToRevenue)
							.map((taxRate) => (
								<Dropdown.Item key={taxRate.xeroTaxRateID} eventKey={taxRate.xeroTaxRateID}>
									{taxRate.name} {taxRate.effectiveRate ? `(${(Number(taxRate.effectiveRate) * 100).toFixed(2)}%)` : ''}
								</Dropdown.Item>
							))}
					</DropdownButton>
				</Col>
			</Form.Group>
			{props.orgMapping?.stripeOrgAccountXeroOrgProcessingType === 'Standard' && (
				<>
					<Form.Group className="pt-2" as={Row}>
						<Col>Choose a Xero Contact to use for guest Stripe payments:</Col>
					</Form.Group>
					<Form.Group as={Row}>
						<Form.Label column sm={2}>
							Xero Contact
						</Form.Label>
						{(props.orgMapping.stripeOrgAccountXeroOrgSettings?.paymentsXeroContact ||
							props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsXeroContactID) && (
							<Col sm={3} className="align-self-center">
								{props.orgMapping.stripeOrgAccountXeroOrgSettings?.paymentsXeroContact?.name ||
									props.mappedXeroOrg!.contacts.find(
										(c) => c.xeroContactID === props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsXeroContactID
									)?.name}
							</Col>
						)}
						<XeroContactLiveSearch
							onConfirm={(values) => {
								props.handleSettingsUpdate(props.stripeOrgID, {
									paymentsXeroContactXeroID: values.xeroContactID,
									paymentsXeroContact: values.xeroContact,
								})
							}}
							accountXeroOrg={props.mappedXeroOrg!}
							appState={props.appState}
							updateMessages={props.updateMessages}
							disabled={!props.orgMapping?.stripeOrgAccountXeroOrgSettings?.paymentsEnabled}
						/>
					</Form.Group>
				</>
			)}
		</>
	)
}

export { PaymentsSettings }
